import styled from 'styled-components';
import {
  Caption,
  Insight,
  SectionHeader,
  SectionWrapper,
  Title,
  StickyHeaderModal,
  ModalTitleContainer,
  ModalTitleMain,
  ModalTitleCaption,
  TextButton as BaseTextButton,
} from './common';
import { AssessmentTestSessionDetailResponse } from '../../../api/admin';
import { useState } from 'react';
import Markdown from '../../Markdown';
import { CollapsibleSectionFooterButton } from '../CollapsibleSection';
import LearningRecommendationsCard from './LearningRecommendationsCard';
import LearningRecommendationsModal from './LearningRecommendationsModal';

const TextButton = styled(BaseTextButton)`
  justify-self: flex-end;
`;

export default function ResultsTakeaways({
  test,
  shouldShowResults,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
  shouldShowResults: boolean;
}) {
  const [openModal, setOpenModal] = useState<string | null>(null);
  return (
    <SectionWrapper>
      <SectionHeader>
        <Title>Analysis Takeaways</Title>
        <Caption>
          Key insights and behavioral patterns from the assessment
        </Caption>
      </SectionHeader>
      <TakeawaysContainer>
        <LeftGrid>
          <StyledInsight
            value={
              shouldShowResults
                ? test?.results?.overallSummary?.keyStrengthsShort ?? ''
                : 'Waiting for results'
            }
            title="Key Strengths"
            icon="fa fa-medal"
            isDisabled={!shouldShowResults}
          >
            {shouldShowResults && (
              <TextButton
                underlined
                onClick={() => setOpenModal('keyStrengths')}
              >
                Full report...
              </TextButton>
            )}
          </StyledInsight>
          <StyledInsight
            value={
              shouldShowResults
                ? test?.results?.overallSummary
                    ?.priorityDevelopmentAreasShort ?? ''
                : 'Waiting for results'
            }
            title="Priority Development Areas"
            icon="fa fa-exclamation-triangle"
            isDisabled={!shouldShowResults}
          >
            {shouldShowResults && (
              <TextButton
                underlined
                onClick={() => setOpenModal('priorityDevelopmentAreas')}
              >
                Full report...
              </TextButton>
            )}
          </StyledInsight>
          <StyledInsight
            title="Cognitive Approach"
            icon="fa fa-brain"
            value={
              shouldShowResults
                ? test?.results?.crossScenarioPatterns
                    ?.cognitiveApproachShort ?? ''
                : 'Waiting for results'
            }
            isDisabled={!shouldShowResults}
          >
            {shouldShowResults && (
              <TextButton
                underlined
                onClick={() => setOpenModal('cognitiveApproach')}
              >
                Full report...
              </TextButton>
            )}
          </StyledInsight>
          <StyledInsight
            title="Behavioral Patterns"
            icon="fa fa-user"
            value={
              shouldShowResults
                ? test?.results?.crossScenarioPatterns
                    ?.behavioralPatternsShort ?? ''
                : 'Waiting for results'
            }
            isDisabled={!shouldShowResults}
          >
            {shouldShowResults && (
              <TextButton
                underlined
                onClick={() => setOpenModal('behavioralPatterns')}
              >
                Full report...
              </TextButton>
            )}
          </StyledInsight>
        </LeftGrid>
        <RightColumn>
          <FeaturedInsight
            value={
              shouldShowResults
                ? 'This personalized learning plan distills the highest-impact modules and projects based on assessment evidence, sequenced to accelerate capability building and address priority gaps.'
                : 'Waiting for results'
            }
            title="Learning Recommendations"
            icon="fa fa-lightbulb"
            isDisabled={!shouldShowResults}
          >
            {shouldShowResults && (
              <>
                <LearningRecommendationsCard
                  items={(
                    test?.results?.learningRecommendations?.learningPlan ?? []
                  ).map((item) => ({
                    type: item.type,
                    slug: item.slug,
                    title: item.title || item?.unit?.title,
                    iconUrl: item.unit?.iconUrl,
                  }))}
                />
                <CollapsibleSectionFooterButton
                  label="View Learning Plan"
                  faIcon="fa-regular fa-arrow-right"
                  onClick={() => setOpenModal('learningRecommendations')}
                />
              </>
            )}
          </FeaturedInsight>
        </RightColumn>
      </TakeawaysContainer>
      <KeyStrengthsModal
        keyStrengths={test?.results?.overallSummary?.keyStrengths ?? ''}
        isOpen={openModal === 'keyStrengths'}
        onClose={() => setOpenModal(null)}
      />
      <PriorityDevelopmentAreasModal
        priorityDevelopmentAreas={
          test?.results?.overallSummary?.priorityDevelopmentAreas ?? ''
        }
        isOpen={openModal === 'priorityDevelopmentAreas'}
        onClose={() => setOpenModal(null)}
      />
      <LearningRecommendationsModal
        explanation={test?.results?.learningRecommendations?.explanation ?? ''}
        learningPlan={
          test?.results?.learningRecommendations?.learningPlan ?? []
        }
        isOpen={openModal === 'learningRecommendations'}
        onClose={() => setOpenModal(null)}
      />
      <CognitiveApproachModal
        cognitiveApproach={
          test?.results?.crossScenarioPatterns?.cognitiveApproach ?? ''
        }
        isOpen={openModal === 'cognitiveApproach'}
        onClose={() => setOpenModal(null)}
      />
      <BehavioralPatternsModal
        behavioralPatterns={
          test?.results?.crossScenarioPatterns?.behavioralPatterns ?? ''
        }
        isOpen={openModal === 'behavioralPatterns'}
        onClose={() => setOpenModal(null)}
      />
    </SectionWrapper>
  );
}

const TakeawaysContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: stretch;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const LeftGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  flex: 2;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const RightColumn = styled.div`
  flex: 1.25;
  display: flex;

  @media (max-width: 1024px) {
    flex: unset;
  }
`;

// Enhanced styled Insight with subtle gradients and better visual hierarchy
const StyledInsight = styled(Insight)`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  /* Style the title with bottom border */
  > div:first-child > p:first-child {
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  }
`;

// Featured Insight for Learning Recommendations - consistent with top cards
const FeaturedInsight = styled(Insight)`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-top: 4px solid ${({ theme }) => theme.colors.buttonPrimary};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  /* Style the title with bottom border */
  > div:first-child > p:first-child {
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};

    /* Color the icon with primary color */
    i {
      color: ${({ theme }) => theme.colors.buttonPrimary};
    }
  }
`;

const KeyStrengthsModal = ({
  keyStrengths,
  isOpen,
  onClose,
}: {
  keyStrengths: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) {
    return null;
  }
  return (
    <StickyHeaderModal
      title={
        <ModalTitleContainer>
          <ModalTitleMain>Key Strengths</ModalTitleMain>
          <ModalTitleCaption>
            Notable capabilities and positive performance indicators
          </ModalTitleCaption>
        </ModalTitleContainer>
      }
      onClose={onClose}
    >
      <Markdown md={keyStrengths} />
    </StickyHeaderModal>
  );
};

const PriorityDevelopmentAreasModal = ({
  priorityDevelopmentAreas,
  isOpen,
  onClose,
}: {
  priorityDevelopmentAreas: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) {
    return null;
  }
  return (
    <StickyHeaderModal
      title={
        <ModalTitleContainer>
          <ModalTitleMain>Priority Development Areas</ModalTitleMain>
          <ModalTitleCaption>
            Key areas for improvement and skill development focus
          </ModalTitleCaption>
        </ModalTitleContainer>
      }
      onClose={onClose}
    >
      <Markdown md={priorityDevelopmentAreas} />
    </StickyHeaderModal>
  );
};

const CognitiveApproachModal = ({
  cognitiveApproach,
  isOpen,
  onClose,
}: {
  cognitiveApproach: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) {
    return null;
  }
  return (
    <StickyHeaderModal
      title={
        <ModalTitleContainer>
          <ModalTitleMain>Cognitive Approach</ModalTitleMain>
          <ModalTitleCaption>
            Problem-solving strategies and thought patterns observed
          </ModalTitleCaption>
        </ModalTitleContainer>
      }
      onClose={onClose}
    >
      <Markdown md={cognitiveApproach} />
    </StickyHeaderModal>
  );
};

const BehavioralPatternsModal = ({
  behavioralPatterns,
  isOpen,
  onClose,
}: {
  behavioralPatterns: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) {
    return null;
  }
  return (
    <StickyHeaderModal
      title={
        <ModalTitleContainer>
          <ModalTitleMain>Behavioral Patterns</ModalTitleMain>
          <ModalTitleCaption>
            Working habits and tendencies demonstrated during assessment
          </ModalTitleCaption>
        </ModalTitleContainer>
      }
      onClose={onClose}
    >
      <Markdown md={behavioralPatterns} />
    </StickyHeaderModal>
  );
};
